/**
 * Exclusive fragment-fate accounting.
 *
 * Every source particle of material must end in exactly one bucket.  A bucket
 * carries both volume and mass because visual particle volume and rigid-body
 * mass may be budgeted independently while still being checked together.
 */

export interface FateQuantity {
  readonly volume: number;
  readonly mass: number;
}

export interface FragmentFateAccounting {
  readonly source: FateQuantity;
  readonly attached: FateQuantity;
  readonly physicalChunks: FateQuantity;
  readonly visualShards: FateQuantity;
  readonly visualGrains: FateQuantity;
  readonly airborneFines: FateQuantity;
  readonly depositedResidue: FateQuantity;
  readonly retiredMaterial: FateQuantity;
}

export interface ConservationTolerance {
  /** Absolute allowance in volume (m³). */
  readonly absoluteVolume: number;
  /** Absolute allowance in mass (kg). */
  readonly absoluteMass: number;
  /** Relative allowance against the larger source/allocated quantity. */
  readonly relative: number;
}

export interface ConservationReport {
  readonly source: FateQuantity;
  readonly allocated: FateQuantity;
  readonly volumeError: number;
  readonly massError: number;
  readonly volumeLimit: number;
  readonly massLimit: number;
  readonly volumeWithinTolerance: boolean;
  readonly massWithinTolerance: boolean;
  readonly conserved: boolean;
}

export const EXACT_CONSERVATION_TOLERANCE: ConservationTolerance = Object.freeze({
  absoluteVolume: 0,
  absoluteMass: 0,
  relative: 0,
});

const FATE_KEYS = [
  'attached',
  'physicalChunks',
  'visualShards',
  'visualGrains',
  'airborneFines',
  'depositedResidue',
  'retiredMaterial',
] as const;

function checkedNumber(value: number, label: string): number {
  if (!Number.isFinite(value) || value < 0) {
    throw new RangeError(`${label} must be finite and non-negative`);
  }
  return value;
}

function checkedQuantity(value: FateQuantity, label: string): FateQuantity {
  if (!value || typeof value !== 'object') throw new TypeError(`${label} is required`);
  return Object.freeze({
    volume: checkedNumber(value.volume, `${label}.volume`),
    mass: checkedNumber(value.mass, `${label}.mass`),
  });
}

function checkedTolerance(tolerance: ConservationTolerance): ConservationTolerance {
  if (!tolerance || typeof tolerance !== 'object') throw new TypeError('Conservation tolerance is required');
  return Object.freeze({
    absoluteVolume: checkedNumber(tolerance.absoluteVolume, 'tolerance.absoluteVolume'),
    absoluteMass: checkedNumber(tolerance.absoluteMass, 'tolerance.absoluteMass'),
    relative: checkedNumber(tolerance.relative, 'tolerance.relative'),
  });
}

/** Inspect an accounting snapshot and report exact errors without changing it. */
export function checkFragmentFateConservation(
  accounting: FragmentFateAccounting,
  tolerance: ConservationTolerance,
): ConservationReport {
  const source = checkedQuantity(accounting.source, 'source');
  const buckets = FATE_KEYS.map((key) => checkedQuantity(accounting[key], key));
  const normalizedTolerance = checkedTolerance(tolerance);
  const sums = buckets.reduce(
    (total, bucket) => ({ volume: total.volume + bucket.volume, mass: total.mass + bucket.mass }),
    { volume: 0, mass: 0 },
  );
  const volumeError = Math.abs(sums.volume - source.volume);
  const massError = Math.abs(sums.mass - source.mass);
  const volumeLimit = normalizedTolerance.absoluteVolume + normalizedTolerance.relative * Math.max(source.volume, sums.volume);
  const massLimit = normalizedTolerance.absoluteMass + normalizedTolerance.relative * Math.max(source.mass, sums.mass);
  const volumeWithinTolerance = volumeError <= volumeLimit;
  const massWithinTolerance = massError <= massLimit;
  return Object.freeze({
    source,
    allocated: Object.freeze(sums),
    volumeError,
    massError,
    volumeLimit,
    massLimit,
    volumeWithinTolerance,
    massWithinTolerance,
    conserved: volumeWithinTolerance && massWithinTolerance,
  });
}

/** Throw if a fate ledger undercounts or double-counts either unit. */
export function assertFragmentFateConserved(
  accounting: FragmentFateAccounting,
  tolerance: ConservationTolerance,
): ConservationReport {
  const report = checkFragmentFateConservation(accounting, tolerance);
  if (!report.conserved) {
    throw new RangeError(
      `Fragment fate is not conserved (volume error ${report.volumeError}, mass error ${report.massError})`,
    );
  }
  return report;
}

/** Construct an immutable, conservation-checked snapshot. */
export function createFragmentFateAccounting(
  input: FragmentFateAccounting,
  tolerance: ConservationTolerance = EXACT_CONSERVATION_TOLERANCE,
): FragmentFateAccounting {
  const normalized = Object.freeze({
    source: checkedQuantity(input.source, 'source'),
    attached: checkedQuantity(input.attached, 'attached'),
    physicalChunks: checkedQuantity(input.physicalChunks, 'physicalChunks'),
    visualShards: checkedQuantity(input.visualShards, 'visualShards'),
    visualGrains: checkedQuantity(input.visualGrains, 'visualGrains'),
    airborneFines: checkedQuantity(input.airborneFines, 'airborneFines'),
    depositedResidue: checkedQuantity(input.depositedResidue, 'depositedResidue'),
    retiredMaterial: checkedQuantity(input.retiredMaterial, 'retiredMaterial'),
  });
  assertFragmentFateConserved(normalized, tolerance);
  return normalized;
}
